import { useState, useEffect, useRef } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import {
  formatDisplayDate,
  parseDateString,
} from "../../../../utils/dateFormatter";
import { useUnsavedChanges } from "../../components/UnsavedChangesProvider";
import CitySuggestionInputField from "../components/CitySuggestionInputField";
import { trackApiCall, trackButtonClick } from "@/utils/analytics";
// import "./DynamicModal.css"

const DynamicModal = ({
  show,
  title,
  mode,
  fields,
  onSubmit,
  location = "profile",
  handleClose,
  initialValues,
  licenseLoading,
  errorSubmitting,
  setLicenseCityDropDown,
}) => {
  const [formValues, setFormValues] = useState({
    licenseNumber: "",
    stateId: "",
    agentLicenseAddress1: "",
    agentLicenseAddress2: "",
    agentLicenseCity: "",
    agentLicenseZip: "",
    affiliatedCompany: "",
    agentLicenseState: "",
    licenseExpiryDate: "",
    primaryAgentLicenceId: "",
    cityFromDropDown: false,
  });

  const [formErrors, setFormErrors] = useState({});
  const errorRef = useRef(null);
  const { setHasUnsavedChanges } = useUnsavedChanges() || {};
  const [filledFieldsCount, setFilledFieldsCount] = useState(1);
  const [previouslyFilledFields, setPreviouslyFilledFields] = useState("");

  // Update form values when initialValues change
  useEffect(() => {
    if (initialValues) {
      setFormValues({
        licenseNumber: initialValues.licenseNumber || "",
        stateId: initialValues.stateId || "",
        agentLicenseAddress1: initialValues.agentLicenseAddress1 || "",
        agentLicenseAddress2: initialValues.agentLicenseAddress2 || "",
        agentLicenseCity: initialValues.agentLicenseCity || "",
        agentLicenseZip: initialValues.agentLicenseZip || "",
        affiliatedCompany: initialValues.affiliatedCompany || "",
        agentLicenseState: initialValues.agentLicenseState || "",
        licenseExpiryDate: initialValues.licenseExpiryDate
          ? formatDisplayDate(initialValues.licenseExpiryDate)
          : "",
        primaryAgentLicenceId: initialValues.primaryAgentLicenceId || "",
        cityFromDropDown: initialValues.cityFromDropDown || false,
      });
    }
  }, [initialValues]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (setHasUnsavedChanges) {
      setHasUnsavedChanges(true);
    }

    if (name === "agentLicenseState") {
      setLicenseCityDropDown(value);
    }

    setFormValues((prevValues) => {
      const newValues = {
        ...prevValues,
        [name]: type === "checkbox" ? checked : value,
      };

      return newValues;
    });
  };

  const validateErrors = (formData, fieldName = null) => {
    const errors = {};

    const validateField = (field) => {
      switch (field) {
        case "licenseNumber":
          if (formData?.licenseNumber?.length === 0) {
            errors.licenseNumber = "This field is required.";
          } else if (
            !/^[a-zA-Z0-9\-#,.()&'"` ]*$/.test(formData.licenseNumber)
          ) {
            errors.licenseNumber = `Characters allowed a-z A-Z 0-9 and - # , . () & ' " ${"`"}`;
          }

          break;

        case "affiliatedCompany":
          if (formData?.affiliatedCompany?.length === 0) {
            errors.affiliatedCompany = "This field is required.";
          } else if (
            !/^[a-zA-Z0-9\\\/_#;,@'\- ]*$/.test(formData.affiliatedCompany)
          ) {
            errors.affiliatedCompany = `Characters allowed: a-z A-Z 0-9 \\ / _ # ; , @ ' -`;
          }
          break;

        case "stateId":
          if (formData?.stateId?.length === 0) {
            errors.stateId = "This field is required.";
          } else if (!/^[A-Z]{2}$/i.test(formData.stateId)) {
            errors.stateId = "Please enter a valid two-letter state code";
          }
          break;

        case "licenseExpiryDate":
          if (formData?.licenseExpiryDate?.length === 0) {
            errors.licenseExpiryDate = "This field is required.";
          } else {
            const currentDate = new Date();
            const expiryDate = new Date(formData.licenseExpiryDate);

            if (isNaN(expiryDate.getTime())) {
              errors.licenseExpiryDate = "Please enter a valid date";
            } else if (expiryDate < currentDate) {
              errors.licenseExpiryDate =
                "License has expired. Please enter a future date";
            }
          }
          break;
        case "agentLicenseZip":
          if (!/^[0-9]{5}$/i.test(formData.agentLicenseZip)) {
            errors.agentLicenseZip = "Please enter a valid zipcode";
          }
          break;
        case "agentLicenseAddress2":
          if (!/^[a-zA-Z0-9#(),'\s]*$/.test(formData.agentLicenseAddress2)) {
            errors.agentLicenseAddress2 = "Please enter a valid street address";
          }
          break;
        case "agentLicenseAddress1":
          if (!/^[a-zA-Z0-9#(),'\s]*$/.test(formData.agentLicenseAddress1)) {
            errors.agentLicenseAddress1 = "Please enter a valid address.";
          }
          break;
      }
    };

    if (fieldName) {
      validateField(fieldName);
    } else {
      [
        "licenseNumber",
        "affiliatedCompany",
        "stateId",
        "licenseExpiryDate",
      ].forEach((field) => validateField(field));
    }

    return {
      errors,
      isValid: Object.keys(errors).length === 0,
    };
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const validationResult = validateErrors({ [name]: value }, name);
    setFormErrors((prev) => ({
      ...prev,
      [name]: validationResult.errors[name],
    }));

    trackButtonClick({
      eventName: "license_info_update",
      eventCategory:
        location === "profile"
          ? "Profile-Forms My Shopper"
          : "Profile Forms Global Alerts",
      eventSource: "on license field change",
      eventLabel: `Field Name : userTypeRadioAgent | Event Number : ${
        filledFieldsCount || 1
      } | Previously filled Field Names : ${
        previouslyFilledFields || ""
      } | user Type:Agent| Action Type : new`,
    });

    setFilledFieldsCount((prev) => prev + 1);

    // Update previously filled fields
    setPreviouslyFilledFields((prev) => (prev ? `${prev}, ${name}` : name));
  };

  // const handleInputChange = (e) => {
  //     const { name, value, type, checked } = e.target;

  //     setFormValues(prevValues => {
  //         const newValues = {
  //             ...prevValues,
  //             [name]: type === 'checkbox' ? checked : value
  //         };

  //         // Set all form values
  //         if (name === 'licenseId') {
  //             newValues.licenseId = value;
  //         }
  //         if (name === 'affiliatedCompany') {
  //             newValues.affiliatedCompany = value;
  //         }
  //         if (name === 'stateId') {
  //             newValues.stateId = value;
  //         }
  //         if (name === 'expirationDate') {
  //             newValues.expirationDate = value;
  //         }
  //         if (name === 'agentLicenseAddress1') {
  //             newValues.agentLicenseAddress1 = value;
  //         }
  //         if (name === 'expirationDate') {
  //             newValues.expirationDate = value;
  //         }
  //         if (name === 'expirationDate') {
  //             newValues.expirationDate = value;
  //         }

  //         console.log('Updated Form Values:', newValues); // Debug log
  //         return newValues;
  //     });
  // };

  const handleModalFormSubmit = async (e) => {
    e.preventDefault(); // Prevent form submission default behavior
    // Verify onSubmit is a function
    if (typeof onSubmit !== "function") {
      console.error("onSubmit prop is not a function");
      return;
    }
    const { errors, isValid } = validateErrors(formValues);

    if (!isValid) {
      setFormErrors(errors);
      return;
    }
    // Create payload object with required fields
    const payload = {
      licenceNumber: formValues.licenseNumber,
      stateId: formValues.stateId,
      agentLicenseAddress1: formValues.agentLicenseAddress1,
      agentLicenseAddress2: formValues.agentLicenseAddress2,
      agentLicenseCity: formValues.agentLicenseCity,
      agentLicenseZip: formValues.agentLicenseZip,
      affiliatedCompany: formValues.affiliatedCompany,
      agentLicenseState: formValues.agentLicenseState,
      licenceExpiryDate: formValues.licenseExpiryDate
        ? formatDisplayDate(formValues?.licenseExpiryDate)
        : "",
      cityFromDropDown: formValues.cityFromDropDown,
    };
    const editPayload = {
      ...payload,
      primaryAgentLicenceId: initialValues?.licenceId || "",
    };
    try {
      // Pass the form values to parent component
      await onSubmit(mode === "Add" ? payload : editPayload);

      // if(errorSubmitting?.length===0){
      //     handleClose();
      // }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Handle error (show error message, etc.)
    }
  };

  const handleModalClose = () => {
    setFormErrors({});
    handleClose();
  };

  useEffect(() => {
    if (errorRef.current) {
      errorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }, [errorSubmitting]);

  return (
    <Modal
      scrollable
      show={show}
      onHide={handleModalClose}
      size="lg"
      dialogClassName="dynamic-modal-body"
    >
      <Modal.Header className="add-agent-license">
        <Modal.Title className="add-agent-license-title">
          {title || (mode === "add" ? "Add New License" : "Update License")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="dynamics-modal-body">
        <Form
          onSubmit={handleModalFormSubmit}
          className="px-5 pt-3 agent-form-modal"
        >
          {" "}
          {/* Add onSubmit here */}
          {errorSubmitting?.length > 0 && (
            <p ref={errorRef} className="text-danger">
              {errorSubmitting}
            </p>
          )}
          {fields.map((field, index) => {
            if (field.type === "header") {
              return (
                <p key={index} className="mt-4 mb-3 ofc-address-heading">
                  {field.label}
                </p>
              );
            }

            return (
              <Form.Group
                key={index}
                className="mb-3 d-flex align-items-center"
                controlId={`form${field.name}`}
              >
                <Form.Label
                  className="col-4"
                  style={{
                    fontSize: "12px",
                    textAlign: "left",
                    alignSelf: "center",
                    fontFamily: "robotolight",
                    fontWeight: 700,
                    color: "#505050",
                  }}
                >
                  {field.label}
                  {field.required && (
                    <span className="text-danger mx-1">* </span>
                  )}
                </Form.Label>
                {field.type === "select" ? (
                  <div className="w-100">
                    <Form.Select
                      name={field.name}
                      // required={field.required}
                      value={formValues[field.name] || ""}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`${
                        formErrors?.[field.name] ? "error-border" : ""
                      } h50 select-agent form-field-style`}
                    >
                      {field.name === "agentLicenseState" ||
                      field.name === "stateId" ? (
                        <>
                          <option value="">{field.label}</option>
                          {field?.options.map((option, i) => (
                            <option key={i} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </>
                      ) : (
                        <>
                          <option value="">{field.label}</option>
                          {field?.options.map((option, i) => (
                            <option key={i} value={option}>
                              {option}
                            </option>
                          ))}
                        </>
                      )}
                    </Form.Select>

                    {formErrors?.[field.name] && (
                      <Form.Text className="input-field-errors">
                        {formErrors[field.name]}
                      </Form.Text>
                    )}
                  </div>
                ) : field.type === "date" ? (
                  <div className="w-100">
                    <Form.Control
                      type="date"
                      placeholder={field.placeholder}
                      name={field.name}
                      // required={field.required}
                      // Convert the display format back to YYYY-MM-DD for the input
                      // value={
                      //   formValues[field.name]
                      //     ? (() => {
                      //         const [month, day, year] =
                      //           formValues[field.name].split("/");
                      //         return `${year}-${month.padStart(
                      //           2,
                      //           "0"
                      //         )}-${day?.padStart(2, "0")}`;
                      //       })()
                      //     : ""
                      // }
                      value={
                        formValues[field.name]
                          ? (() => {
                              const dateStr = formValues[field.name];
                              if (dateStr.includes("-")) return dateStr; // Already in YYYY-MM-DD format
                              const date = new Date(dateStr);
                              if (isNaN(date.getTime())) return "";
                              const year = date.getFullYear();
                              const month = String(
                                date.getMonth() + 1
                              ).padStart(2, "0");
                              const day = String(date.getDate()).padStart(
                                2,
                                "0"
                              );
                              return `${year}-${month}-${day}`;
                            })()
                          : ""
                      }
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      className={`${
                        formErrors?.[field.name] ? "is-invalid" : ""
                      } placeholder-agent form-field-style`}
                    />
                    {formErrors?.[field.name] && (
                      <Form.Text className="input-field-errors">
                        {formErrors[field.name]}
                      </Form.Text>
                    )}
                  </div>
                ) : field.name === "agentLicenseCity" ? (
                  <CitySuggestionInputField
                    name={field.name}
                    type={field.type}
                    placeholder={field.placeholder}
                    value={formValues[field.name] || ""}
                    onChange={handleInputChange}
                    required={field.required}
                    className={`${
                      formErrors?.[field.name] ? "is-invalid" : ""
                    } placeholder-agent form-field-style`}
                    suggestions={field.options}
                    onBlur={handleBlur}
                  />
                ) : (
                  <div className="w-100">
                    <Form.Control
                      type={field.type}
                      placeholder={field.placeholder}
                      name={field.name}
                      // required={field.required}
                      value={formValues[field.name] || ""}
                      onChange={handleInputChange}
                      pattern={field.pattern}
                      maxLength={field.maxLength}
                      onBlur={handleBlur}
                      className={`${
                        formErrors?.[field.name] ? "is-invalid" : ""
                      } form-field-style placeholder-agent`}
                      style={{
                        paddingRight: "10px",
                      }}
                    />
                    {formErrors?.[field.name] && (
                      <Form.Text className="input-field-errors">
                        {formErrors[field.name]}
                      </Form.Text>
                    )}
                  </div>
                )}
              </Form.Group>
            );
          })}
          <div className="mt-4 ms-5 ps-4 d-flex justify-content-center">
            <Button
              variant="primary"
              className="me-2 update-license fs-16"
              type="submit" // Changed to type="submit"
              disabled={licenseLoading}
            >
              {mode === "Add" ? (
                <>{licenseLoading ? "Add License..." : "Add License"}</>
              ) : (
                <>{licenseLoading ? "Update License..." : "Update License"}</>
              )}
            </Button>
            <Button
              onClick={handleModalClose}
              className="me-2 cancel-license"
              type="button" // Explicitly set type="button"
            >
              Cancel
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default DynamicModal;
